import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfToday, subDays } from 'date-fns';
import { CalendarIcon, Check, Copy, ExternalLink, Ticket } from 'lucide-react';
import TablePagination from '@mui/material/TablePagination';

import { Calendar } from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast as newToast } from '@/components/ui/sonner';
import {
  Button,
  CellStack,
  Column,
  DataTable,
  EmptyState,
  Field,
  FieldGrid,
  FilterTabs,
  IconButton,
  PageHeader,
  SidePanel,
  StatusBadge,
  Surface,
  Text,
  Toolbar,
  ToolbarControl,
} from '@/components/flup';

import { cn, getSlotTimeRange } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';
import { getVendorsBookingsWithPagination } from '@/store/thunkMidddleware/turfMiddleware';
import { Values } from '@/constants/Values';
import { useDebounce } from '@/hooks/useDebounce';

/**
 * The bookings list — the screen the reference's order table maps onto most
 * directly.
 *
 * Clicking a row opens a summary panel beside the list rather than navigating
 * away. That matters because the common task here is triage: scan a day, glance
 * at three or four bookings, act on one. A full page navigation per booking
 * turns that into a dozen round trips. The full booking page is still one click
 * from inside the panel, for when a vendor genuinely needs it.
 */

type BookingStatus = 'all' | 'upcoming' | 'completed' | 'cancelled';

/**
 * Where a booking came from.
 *
 * These are the three sources a vendor's bookings actually carry, and they
 * settle differently — a VENDOR booking was taken at the counter, MARKETPLACE
 * came through BookYourZone, DBL through the venue's own link — so a vendor
 * reconciling a day's takings needs them apart. The server also knows APP, WEB
 * and OFFLINE; nothing writes them, so listing them would be three dead
 * filters.
 */
type BookingSource = 'all' | 'VENDOR' | 'MARKETPLACE' | 'DBL';

const SOURCES: { key: BookingSource; label: string }[] = [
  { key: 'all', label: 'All source' },
  { key: 'VENDOR', label: 'Taken by you' },
  { key: 'MARKETPLACE', label: 'From App' },
  { key: 'DBL', label: 'Direct booking link' },
];

const sourceLabel = (key: BookingSource) =>
  SOURCES.find((s) => s.key === key)?.label ?? 'Any source';

const today = startOfToday();
const minDate = subDays(today, 30);

const TABS: { key: BookingStatus; label: string }[] = [
  { key: 'all', label: 'All bookings' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const customerName = (b: any) =>
  `${b?.userDetails?.firstName || 'Player'} ${b?.userDetails?.lastName || ''}`.trim();

const dateOnly = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const Bookings = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const response = useAppSelector(
    (state: RootState) => state.turf?.getVendorsBookingsWithPaginationResponse,
  );

  const [activeTab, setActiveTab] = useState<BookingStatus>('all');
  const [itemPerPage, setItemPerPage] = useState<number>(
    response?.payload?.itemsPerPage || Values.BookingLimit,
  );
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState<Date | null>(null);
  const [source, setSource] = useState<BookingSource>('all');
  const [openBooking, setOpenBooking] = useState<any | null>(null);

  const debouncedSearch = useDebounce(search, 500);

  const fetchBookings = (
    page = 1,
    status: string = 'all',
    limit = Values.BookingLimit,
    bookingId = '',
    date?: Date | null,
    bookingSource: BookingSource = 'all',
  ) => {
    dispatch(
      getVendorsBookingsWithPagination({
        queryParams: {
          page,
          limit,
          status,
          bookingId,
          date: date ? date.toISOString() : undefined,
          // Filtering happens server-side: the list is paginated, so narrowing
          // the page in hand would only ever filter the rows already fetched.
          bookingSource: bookingSource === 'all' ? undefined : bookingSource,
        },
      }),
    );
  };

  useEffect(() => {
    fetchBookings(1, activeTab, itemPerPage, debouncedSearch, dateFilter, source);
    // `itemPerPage` changes go through their own handler, which refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, debouncedSearch, dateFilter, source]);

  const bookings: any[] = response?.payload?.bookings || [];
  const totalItems = response?.payload?.totalItems ?? 0;
  const currentPage = response?.payload?.currentPage ?? 1;

  const copyLink = async (link?: string) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      newToast.success('Payment link copied', { closeButton: true });
    } catch {
      newToast.error('Could not copy the link');
    }
  };

  const columns: Column<any>[] = useMemo(
    () => [
      {
        key: 'ref',
        header: 'Booking ID',
        width: 'min-w-[150px]',
        render: (b) => (
          <CellStack primary={b?.bookingRef || b?._id} secondary={b?.bookingSource} />
        ),
      },
      {
        key: 'customer',
        header: 'Customer',
        width: 'min-w-[160px]',
        render: (b) => (
          <CellStack primary={customerName(b)} secondary={b?.userDetails?.phoneNumber} />
        ),
      },
      {
        key: 'venue',
        header: 'Venue',
        hideBelow: 'lg',
        width: 'min-w-[150px]',
        render: (b) => (
          <CellStack primary={b?.venueName} secondary={b?.sportsType?.toUpperCase()} />
        ),
      },
      {
        key: 'scheduledFor',
        header: 'Scheduled',
        sortable: true,
        width: 'min-w-[150px]',
        render: (b) => (
          <CellStack primary={dateOnly(b?.scheduledFor)} secondary={getSlotTimeRange(b?.slot)} />
        ),
      },
      {
        key: 'bookedOn',
        header: 'Booked on',
        hideBelow: 'xl',
        render: (b) => (
          <Text variant="body" tone="secondary" as="span">
            {dateOnly(b?.bookingDateAndTime)}
          </Text>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (b) => <StatusBadge status={b?.status} />,
      },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        render: (b) => (
          <Text variant="bodySemi" as="span" className="nums">
            ₹{Number(b?.paymentSplit?.venuePayableRupee ?? 0).toFixed(2)}
          </Text>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bookings"
        count={totalItems ? `${totalItems} bookings` : undefined}
        subtitle="Every booking across your venues, newest first."
      />

      <FilterTabs
        items={TABS.map((t) => ({ key: t.key, label: t.label }))}
        value={activeTab}
        onChange={(key) => setActiveTab(key as BookingStatus)}
      />

      <Toolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by booking ID or reference"
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button">
              <ToolbarControl
                icon={<Ticket className="w-4 h-4" />}
                label={sourceLabel(source)}
                active={source !== 'all'}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {SOURCES.map((s) => (
              <DropdownMenuItem
                key={s.key}
                onSelect={() => setSource(s.key)}
                className="justify-between gap-2"
              >
                {s.label}
                {source === s.key ? <Check className="w-3.5 h-3.5" /> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Popover>
          <PopoverTrigger asChild>
            <button type="button">
              <ToolbarControl
                icon={<CalendarIcon className="w-4 h-4" />}
                label={dateFilter ? format(dateFilter, 'dd MMM yyyy') : 'Any date'}
                active={Boolean(dateFilter)}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={dateFilter ?? undefined}
              onSelect={(d) => setDateFilter(d ?? null)}
              defaultMonth={today}
              fromDate={minDate}
              disabled={{ before: minDate }}
              className={cn('p-3 pointer-events-auto')}
            />
          </PopoverContent>
        </Popover>

        {dateFilter ? (
          <Button variant="ghost" onClick={() => setDateFilter(null)}>
            Clear date
          </Button>
        ) : null}
      </Toolbar>

      <DataTable
        columns={columns}
        rows={bookings}
        rowKey={(b) => String(b?._id)}
        loading={response.isLoading}
        selectedKey={openBooking?._id ?? null}
        onRowClick={(b) => setOpenBooking(b)}
        empty={
          <EmptyState
            icon={<Ticket />}
            title="No bookings match these filters"
            message={
              search || dateFilter || source !== 'all'
                ? 'Try clearing the search, the date or the source filter.'
                : 'Bookings will appear here as customers reserve your slots.'
            }
            actionLabel={search || dateFilter || source !== 'all' ? 'Clear filters' : undefined}
            onAction={
              search || dateFilter || source !== 'all'
                ? () => {
                    setSearch('');
                    setDateFilter(null);
                    setSource('all');
                  }
                : undefined
            }
          />
        }
      />

      {totalItems > 0 ? (
        <Surface padding="px-2">
          <TablePagination
            rowsPerPageOptions={[10, 20]}
            component="div"
            count={totalItems}
            rowsPerPage={itemPerPage}
            page={Math.max(0, currentPage - 1)}
            onPageChange={(_, page) =>
              fetchBookings(page + 1, activeTab, itemPerPage, debouncedSearch, dateFilter)
            }
            onRowsPerPageChange={(e) => {
              const newLimit = parseInt(e.target.value, 10);
              setItemPerPage(newLimit);
              fetchBookings(1, activeTab, newLimit, debouncedSearch, dateFilter);
            }}
          />
        </Surface>
      ) : null}

      <BookingPanel
        booking={openBooking}
        onClose={() => setOpenBooking(null)}
        onCopyLink={copyLink}
        onOpenFull={(id) =>
          navigate(`/dashboard/booking-details/${id}`, { state: { bookingid: id } })
        }
      />
    </div>
  );
};

/** The summary that opens beside the list, so triage doesn't cost a page load. */
const BookingPanel: React.FC<{
  booking: any | null;
  onClose: () => void;
  onCopyLink: (link?: string) => void;
  onOpenFull: (id: string) => void;
}> = ({ booking, onClose, onCopyLink, onOpenFull }) => {
  if (!booking) return null;

  const amount = Number(booking?.paymentSplit?.venuePayableRupee ?? 0);
  const paidAtVenue = String(booking?.bookingSource || '').trim() === 'VENDOR';

  return (
    <SidePanel
      open={Boolean(booking)}
      onClose={onClose}
      title={booking?.bookingRef || booking?._id}
      subtitle={customerName(booking)}
      actions={
        <IconButton
          label="Open full booking"
          size="sm"
          onClick={() => onOpenFull(booking._id)}
        >
          <ExternalLink className="w-4 h-4" />
        </IconButton>
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <div>
            <Text variant="fieldLabel" tone="tertiary" as="p">
              {paidAtVenue ? 'Paid at venue' : 'Pending amount'}
            </Text>
            <Text variant="stat" as="p">
              ₹{amount.toFixed(2)}
            </Text>
          </div>
          <Button onClick={() => onOpenFull(booking._id)}>Open booking</Button>
        </div>
      }
    >
      <div className="flex items-center gap-2">
        <StatusBadge status={booking?.status} />
        <Text variant="caption" tone="tertiary" as="span">
          Booked {dateOnly(booking?.bookingDateAndTime)}
        </Text>
      </div>

      <div className="rounded-flup-md border border-flup-hairline p-4">
        <FieldGrid>
          <Field label="Venue" value={booking?.venueName} />
          <Field label="Sport" value={booking?.sportsType?.toUpperCase()} />
          <Field label="Scheduled for" value={dateOnly(booking?.scheduledFor)} />
          <Field label="Time" value={getSlotTimeRange(booking?.slot)} />
          <Field label="Source" value={booking?.bookingSource} />
          <Field label="Court" value={booking?.courtName} />
        </FieldGrid>
      </div>

      <div className="rounded-flup-md border border-flup-hairline p-4">
        <Text variant="fieldLabel" tone="tertiary" as="p" className="mb-3">
          Customer
        </Text>
        <FieldGrid>
          <Field label="Name" value={customerName(booking)} />
          <Field label="Phone" value={booking?.userDetails?.phoneNumber} />
          <Field label="Email" value={booking?.userDetails?.email} wide />
        </FieldGrid>
      </div>

      {booking?.paymentLink ? (
        <div className="rounded-flup-md border border-flup-hairline p-4">
          <Text variant="fieldLabel" tone="tertiary" as="p" className="mb-2">
            Payment link
          </Text>
          <div className="flex items-center gap-2">
            <a
              href={booking.paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate"
            >
              <Text variant="caption" tone="info" as="span" className="truncate">
                {booking.paymentLink}
              </Text>
            </a>
            <IconButton label="Copy link" size="sm" onClick={() => onCopyLink(booking.paymentLink)}>
              <Copy className="w-3.5 h-3.5" />
            </IconButton>
          </div>
        </div>
      ) : null}
    </SidePanel>
  );
};

export default Bookings;
